/**
 * Stage 8: WordPress Publish — context-based version using StagePanel.
 *
 * Publishes the article as a draft to WordPress via REST API. Non-LLM stage.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { EditorialReviewBanner } from './EditorialReviewBanner';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const seo = document?.article?.seo;
  const category = document?.thesis?.wordpress_category;
  const image = document?.article?.featured_image;
  const markdown = document?.article?.markdown;
  const wp = document?.preflight?.wordpress;

  const warnings: string[] = [];
  if (!category) warnings.push('No WordPress category set on candidate.');
  if (!markdown) warnings.push('No article markdown — run earlier stages first.');
  if (!image || (image.s3_keys || []).length === 0) warnings.push('No featured image generated.');

  return (
    <div className="space-y-3">
      <EditorialReviewBanner />
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-gray-500">Category:</span>{' '}
          <span className="text-gray-700">{category?.name || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Slug:</span>{' '}
          <span className="text-gray-700">{seo?.slug || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Meta title:</span>{' '}
          <span className="text-gray-700">{seo?.meta_title || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Meta description:</span>{' '}
          <span className="text-gray-700">{seo?.meta_description || '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Featured image:</span>{' '}
          <span className="text-gray-700">
            {image && (image.s3_keys || []).length > 0
              ? `${(image.s3_keys || []).length} image(s), selected: ${image.selected_index ?? 'none'}`
              : '—'}
          </span>
        </div>
        <div>
          <span className="text-gray-500">Article length:</span>{' '}
          <span className="text-gray-700">
            {markdown ? `${markdown.split(/\s+/).filter(Boolean).length} words` : '—'}
          </span>
        </div>
      </div>
      {warnings.length > 0 && (
        <div className="space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {w}
            </div>
          ))}
        </div>
      )}
      {wp && (wp.post_id || (wp as any).wordpress_post_id) ? (
        <div className="text-xs bg-green-50 border border-green-200 rounded px-2 py-1">
          Previously published: Post #{wp.post_id || (wp as any).wordpress_post_id} —{' '}
          <a href={wp.post_url || (wp as any).wordpress_post_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {wp.post_url || (wp as any).wordpress_post_url}
          </a>
        </div>
      ) : null}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const wp = doc.preflight?.wordpress;
  const postId = wp?.post_id || (wp as any)?.wordpress_post_id;
  if (!wp || !postId) {
    return <div className="text-sm text-gray-500">Not published yet.</div>;
  }
  const postUrl = wp.post_url || (wp as any).wordpress_post_url;
  const previewUrl = (wp as any).wordpress_preview_url;
  const postStatus = wp.post_status || (wp as any).status;

  return (
    <div className="space-y-2">
      <div className="text-xs">
        <span className="font-medium text-gray-700">Post ID:</span>{' '}
        <span className="text-gray-600">{postId}</span>
      </div>
      <div className="text-xs">
        <span className="font-medium text-gray-700">Status:</span>{' '}
        <span className="text-gray-600">{postStatus}</span>
      </div>
      {postUrl && (
        <div className="text-xs">
          <span className="font-medium text-gray-700">URL:</span>{' '}
          <a href={postUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {postUrl}
          </a>
        </div>
      )}
      {previewUrl && (
        <div className="text-xs">
          <span className="font-medium text-gray-700">Preview:</span>{' '}
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {previewUrl}
          </a>
        </div>
      )}
    </div>
  );
}

export function Stage8WpPublishV2() {
  const { document } = useArticleContext();
  const hasMarkdown = Boolean(document?.article?.markdown);
  const hasCategory = Boolean(document?.thesis?.wordpress_category);

  return (
    <StagePanel
      stageId="stage_8_wp_publish"
      title="Stage 8: WordPress Publish"
      description="Publishes the article as a draft to WordPress via REST API."
      runDisabled={!hasMarkdown || !hasCategory}
      runDisabledReason={!hasMarkdown ? 'No article markdown yet' : !hasCategory ? 'No WordPress category set' : undefined}
      runLabel="Publish draft to WordPress"
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
